import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/SkillsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Skill {
  skill_id: number;
  name: string;
}

interface SkillDetails {
  skill_id: number;
  name: string;
  levels?: Array<{
    level_id: number;
    name: string;
  }>;
}

const SkillsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [skillToDelete, setSkillToDelete] = useState<SkillDetails | null>(null);
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Skills", "/skills");
      hasTrackedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/skills`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch skills.");
          return;
        }

        const data: Skill[] = await response.json();
        setSkills(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (selectedOrgID) {
      fetchSkills();
    }
  }, [token, selectedOrgID]);

  const checkSkillUsage = async (skillID: number): Promise<SkillDetails | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/skills/${skillID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to fetch skill details.");
        return null;
      }

      return await response.json();
    } catch (err) {
      console.error("Error fetching skill details:", err);
      return null;
    }
  };

  const handleDeleteClick = async (skillID: number) => {
    const skillDetails = await checkSkillUsage(skillID);
    if (skillDetails) {
      setSkillToDelete(skillDetails);
    }
  };

  const handleDelete = async (skillID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/skills/${skillID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to delete skill.");
        return;
      }

      setSkills(skills.filter((s) => s.skill_id !== skillID));
      setSkillToDelete(null);
    } catch (err) {
      console.error("Error deleting skill:", err);
    }
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  const filteredSkills = skills.filter((skill) =>
    skill.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Skills</h1>

      <div className={styles.searchContainer}>
        <input
          type="text"
          id="search"
          className={styles.searchInput}
          placeholder="Search by skill name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <button onClick={() => navigate("/skills/create")} className={styles.createButton}>
        New Skill
      </button>

      {skillToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setSkillToDelete(null)} />
          <div className={styles.dialog}>
            <div className={styles.dialogContent}>
              {skillToDelete.levels?.length ? (
                <>
                  Skill "<em>{skillToDelete.name}</em>" is used in {skillToDelete.levels.length} level{skillToDelete.levels.length !== 1 ? 's' : ''}.<br /><br />
                  Deleting this skill will remove it from {skillToDelete.levels.length !== 1 ? 'those levels' : 'that level'}, and unlink it from any users that have it.
                </>
              ) : (
                <>
                  Skill "<em>{skillToDelete.name}</em>" isn't used in any levels.
                </>
              )}
              <br /><br />
              This action CANNOT BE UNDONE. Are you sure you want to delete it?
            </div>
            <div className={styles.dialogButtons}>
              <button onClick={() => handleDelete(skillToDelete.skill_id)} className={styles.dialogButton}>
                Yes, Delete
              </button>
              <button onClick={() => setSkillToDelete(null)} className={styles.dialogCancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Skill</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredSkills.length > 0 ? (
            filteredSkills.map((s) => (
              <tr key={s.skill_id} className={styles.tableRow}>
                <td className={styles.tableCell}>
                  <a
                    href="#"
                    className={styles.tableLink}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      navigate(`/skills/${s.skill_id}`);
                    }}
                  >
                    {s.name}
                  </a>
                </td>
                <td className={styles.tableCell}>
                  <button onClick={() => handleDeleteClick(s.skill_id)} className={styles.deleteButton}>
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className={styles.tableCell}>No skills found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SkillsPage;
